import { useState, useEffect, useMemo } from "react";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Printer, AlertTriangle, Cake, Info, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Table, CombinedTable } from "@/types/table";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";
import { isTimeWithinSlot } from "@/lib/serviceOrder";

interface Reservation {
  id: number;
  supabaseId: string;
  time: string;
  name: string;
  persons: number;
  numberOfChildren?: number;
  hasStroller?: boolean;
  tableId: string | undefined;
  combinedTableId: string | undefined;
  status: "planifié" | "arrivé" | "annulé" | "en_attente";
  phone: string;
  email: string | undefined;
  reservationDate: Date;
  createdAt: Date;
  notes: string | undefined;
  internalNotes: string | undefined;
  customAnswers?: { question: string; answers: string[] };
}

interface TimeSlot {
  start: string;
  end: string;
}

interface PrintOptions {
  showTableNumbers: boolean;
  showNotes: boolean;
  showSummary: boolean;
}

interface PrintReservationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: Date;
  reservations: Reservation[];
  tables: Table[];
  combinedTables: CombinedTable[];
  timeSlots: TimeSlot[];
  establishmentName: string;
}

const PRINT_OPTIONS_STORAGE_KEY = "printReservationsOptions";

const defaultOptions: PrintOptions = {
  showTableNumbers: true,
  showNotes: true,
  showSummary: true,
};

export function PrintReservationsDialog({
  open,
  onOpenChange,
  date,
  reservations,
  tables,
  combinedTables,
  timeSlots,
  establishmentName,
}: PrintReservationsDialogProps) {
  const [options, setOptions] = useState<PrintOptions>(() => {
    const stored = localStorage.getItem(PRINT_OPTIONS_STORAGE_KEY);
    if (stored) {
      try {
        return { ...defaultOptions, ...JSON.parse(stored) };
      } catch {
        return defaultOptions;
      }
    }
    return defaultOptions;
  });

  // Day selection state
  const [selectedDay, setSelectedDay] = useState<"today" | "tomorrow">("today");

  // Effective date based on selection
  const effectiveDate = useMemo(() => {
    const today = new Date();
    if (selectedDay === "tomorrow") {
      return addDays(today, 1);
    }
    return today;
  }, [selectedDay]);

  // Save options to localStorage when they change
  useEffect(() => {
    localStorage.setItem(PRINT_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [options]);

  const updateOption = (key: keyof PrintOptions, value: boolean) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  // Filter reservations for the selected date (exclude cancelled)
  const compareServiceTime = useServiceTimeCompare();
  const dayReservations = useMemo(() => {
    return reservations
      .filter(r => 
        format(r.reservationDate, "yyyy-MM-dd") === format(effectiveDate, "yyyy-MM-dd") &&
        r.status !== "annulé"
      )
      .sort((a, b) => compareServiceTime(a.time, b.time));
  }, [reservations, effectiveDate, compareServiceTime]);

  // Group reservations by time slot
  const groupedBySlot = useMemo(() => {
    const groups: { slot: TimeSlot; reservations: Reservation[]; totalPersons: number }[] = [];
    
    timeSlots.forEach(slot => {
      const slotReservations = dayReservations.filter(r => {
        const resTime = r.time;
        return isTimeWithinSlot(resTime, slot);
      });
      
      if (slotReservations.length > 0) {
        groups.push({
          slot,
          reservations: slotReservations,
          totalPersons: slotReservations.reduce((sum, r) => sum + r.persons, 0),
        });
      }
    });

    // Handle reservations outside defined slots
    const outsideSlots = dayReservations.filter(r => {
      return !timeSlots.some(slot => isTimeWithinSlot(r.time, slot));
    });

    if (outsideSlots.length > 0) {
      groups.push({
        slot: { start: "Autres", end: "" },
        reservations: outsideSlots,
        totalPersons: outsideSlots.reduce((sum, r) => sum + r.persons, 0),
      });
    }

    return groups;
  }, [dayReservations, timeSlots]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalPersons = dayReservations.reduce((sum, r) => sum + r.persons, 0);
    const totalReservations = dayReservations.length;
    const withNotes = dayReservations.filter(r => r.notes).length;
    const specialOccasions = dayReservations.filter(r => 
      r.notes?.toLowerCase().includes("anniversaire") || 
      r.notes?.toLowerCase().includes("birthday") ||
      r.notes?.toLowerCase().includes("gâteau") ||
      r.notes?.toLowerCase().includes("fête")
    ).length;
    
    return { totalPersons, totalReservations, withNotes, specialOccasions };
  }, [dayReservations]);

  // Get initials from name
  const getInitials = (name: string): string => {
    return name
      .split(/[\s-]+/)
      .filter(part => part.length > 0)
      .map(part => part[0].toUpperCase())
      .join(".") + ".";
  };

  // Get table name
  const getTableName = (reservation: Reservation): string => {
    if (reservation.tableId) {
      const table = tables.find(t => t.id === reservation.tableId);
      return table?.name || "--";
    }
    if (reservation.combinedTableId) {
      const combined = combinedTables.find(ct => ct.id === reservation.combinedTableId);
      return combined?.name || "--";
    }
    return "--";
  };


  // Check if note contains allergy/dietary info
  const hasAllergyNote = (notes: string | undefined): boolean => {
    if (!notes) return false;
    const allergyKeywords = [
      "allergi", "gluten", "lactose", "végétarien", "vegan", "végan",
      "sans", "intoléran", "halal", "casher", "kosher", "régime"
    ];
    return allergyKeywords.some(kw => notes.toLowerCase().includes(kw));
  };

  // Check if note contains special occasion
  const hasSpecialOccasion = (notes: string | undefined): boolean => {
    if (!notes) return false;
    const occasionKeywords = ["anniversaire", "birthday", "gâteau", "fête", "célébration"];
    return occasionKeywords.some(kw => notes.toLowerCase().includes(kw));
  };

  const getSlotLabel = (slot: TimeSlot): string => {
    if (slot.start === "Autres") return "Autres créneaux";
    
    const startHour = parseInt(slot.start.split(":")[0]);
    if (startHour < 15) return `Service du midi (${slot.start} - ${slot.end})`;
    return `Service du soir (${slot.start} - ${slot.end})`;
  };

  // Escape HTML special characters
  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  // Generate the body content for the print HTML
  const generateBodyContent = (): string => {
    let html = `
      <div class="header">
        <h1>🍽️ RÉSERVATIONS — ${format(effectiveDate, "EEEE d MMMM yyyy", { locale: fr })}</h1>
        <div class="meta">${escapeHtml(establishmentName)}</div>
        <div class="meta" style="font-weight:bold;margin-top:4px;">
          Total : ${totals.totalPersons} couvert${totals.totalPersons > 1 ? 's' : ''} | 
          ${totals.totalReservations} réservation${totals.totalReservations > 1 ? 's' : ''}
        </div>
      </div>
    `;

    if (dayReservations.length === 0) {
      html += `<p style="text-align:center;color:#666;padding:40px 0;">Aucune réservation pour cette date</p>`;
      return html;
    }
    
    // Pour chaque créneau
    groupedBySlot.forEach(({ slot, reservations: slotReservations, totalPersons }) => {
      html += `
        <div class="slot">
          <div class="slot-header">
            <span>${getSlotLabel(slot)}</span>
            <span>${totalPersons} couvert${totalPersons > 1 ? 's' : ''}</span>
          </div>
      `;
      
      slotReservations.forEach(res => {
        const noteClass = hasAllergyNote(res.notes) 
          ? 'note-allergy' 
          : hasSpecialOccasion(res.notes) 
            ? 'note-occasion' 
            : '';
        
        html += `
          <div class="reservation">
            <span class="time">${res.time}</span>
            ${options.showTableNumbers ? `<span class="table">${escapeHtml(getTableName(res))}</span>` : ''}
            <span class="persons">${(res.persons || 0) + (res.numberOfChildren || 0)}${(res.numberOfChildren || 0) > 0 ? ` dont ${res.numberOfChildren}enf` : ''}${res.hasStroller ? ' +poussette' : ''} pers</span>
            <span class="name">${escapeHtml(getInitials(res.name))}</span>
            ${options.showNotes && res.notes ? `<span class="note ${noteClass}">${escapeHtml(res.notes)}</span>` : ''}
            ${options.showNotes && res.customAnswers && res.customAnswers.answers.length > 0 ? `<span class="note">${escapeHtml(res.customAnswers.question)} ${escapeHtml(res.customAnswers.answers.join(', '))}</span>` : ''}
          </div>
        `;
      });
      
      html += '</div>';
    });
    
    // Récapitulatif
    if (options.showSummary) {
      html += `
        <div class="summary">
          <strong>RÉCAPITULATIF</strong><br>
          Total couverts : ${totals.totalPersons}<br>
          ${totals.withNotes > 0 ? `⚠️ Notes/allergies : ${totals.withNotes} réservation${totals.withNotes > 1 ? 's' : ''}<br>` : ''}
          ${totals.specialOccasions > 0 ? `🎂 Occasions spéciales : ${totals.specialOccasions}<br>` : ''}
        </div>
        <div class="footer">
          Imprimé le ${format(new Date(), "dd/MM/yyyy 'à' HH:mm", { locale: fr })}
        </div>
      `;
    }
    
    return html;
  };

  // Generate complete HTML document for printing
  const generatePrintHTML = (): string => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Réservations - ${format(effectiveDate, "d MMMM yyyy", { locale: fr })}</title>
        <style>
          @page {
            size: landscape;
            margin: 8mm;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 11pt;
            color: #000;
            background: #fff;
            line-height: 1.4;
          }
          
          .header {
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 4px;
          }
          
          .header .meta {
            font-size: 10pt;
            color: #666;
          }
          
          .slot {
            margin-bottom: 20px;
          }
          
          .slot-header {
            background: #f0f0f0;
            padding: 8px 12px;
            font-weight: bold;
            font-size: 12pt;
            display: flex;
            justify-content: space-between;
            border: 1px solid #ccc;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .reservation {
            display: flex;
            padding: 6px 12px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 11pt;
            align-items: baseline;
          }
          
          .reservation .time {
            width: 50px;
            font-family: monospace;
            font-weight: bold;
            flex-shrink: 0;
          }
          
          .reservation .table {
            width: 50px;
            font-weight: 500;
            flex-shrink: 0;
          }
          
          .reservation .persons {
            width: 70px;
            flex-shrink: 0;
          }
          
          .reservation .name {
            flex: 1;
            min-width: 0;
          }
          
          .reservation .note {
            max-width: 250px;
            font-size: 10pt;
            text-align: right;
          }
          
          .note-allergy {
            color: #d97706;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .note-occasion {
            color: #db2777;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .summary {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 2px solid #000;
            font-size: 10pt;
          }
          
          .footer {
            margin-top: 15px;
            font-size: 9pt;
            color: #888;
          }
        </style>
      </head>
      <body>
        ${generateBodyContent()}
      </body>
      </html>
    `;
  };

  // Handle print using hidden iframe technique
  const handlePrint = () => {
    const printContent = generatePrintHTML();
    
    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);
    
    // Write content to iframe
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(printContent);
      doc.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        iframe.contentWindow?.print();
        // Cleanup after print dialog closes
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="max-w-4xl">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Imprimer les réservations
          </ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
            {format(effectiveDate, "EEEE d MMMM yyyy", { locale: fr })} • {totals.totalReservations} réservation{totals.totalReservations > 1 ? "s" : ""} • {totals.totalPersons} couvert{totals.totalPersons > 1 ? "s" : ""}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogBody className="space-y-4">
          {/* Day selector */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">Jour à imprimer</h4>
            </div>
            <ToggleGroup
              type="single"
              value={selectedDay}
              onValueChange={(value) => value && setSelectedDay(value as "today" | "tomorrow")}
              className="justify-start"
            >
              <ToggleGroupItem value="today" aria-label="Aujourd'hui" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                Aujourd'hui
              </ToggleGroupItem>
              <ToggleGroupItem value="tomorrow" aria-label="Demain" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                Demain
              </ToggleGroupItem>
            </ToggleGroup>
            
            {/* Warning message */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Les réservations peuvent changer après l'impression. 
                Pensez à <strong>réimprimer</strong> en cas de nouvelles réservations ou modifications.
              </p>
            </div>
          </div>

          <Separator />

          {/* Options */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm">Options d'affichage</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showTableNumbers"
                  checked={options.showTableNumbers}
                  onCheckedChange={(checked) => updateOption("showTableNumbers", !!checked)}
                />
                <Label htmlFor="showTableNumbers" className="text-sm">Numéros de table</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showNotes"
                  checked={options.showNotes}
                  onCheckedChange={(checked) => updateOption("showNotes", !!checked)}
                />
                <Label htmlFor="showNotes" className="text-sm">Notes clients (allergies, etc.)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showSummary"
                  checked={options.showSummary}
                  onCheckedChange={(checked) => updateOption("showSummary", !!checked)}
                />
                <Label htmlFor="showSummary" className="text-sm">Récapitulatif</Label>
              </div>
            </div>
          </div>

          <Separator />

          {/* Preview - Uses same HTML generation for consistency */}
          <div className="text-sm text-muted-foreground mb-2">Prévisualisation</div>
          
          <div className="border rounded-lg p-4 bg-white text-foreground max-h-[400px] overflow-y-auto">
            {/* Header */}
            <div className="print-header border-b-2 border-foreground pb-3 mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                🍽️ RÉSERVATIONS — {format(effectiveDate, "EEEE d MMMM yyyy", { locale: fr })}
              </h2>
              <p className="text-sm text-muted-foreground">{establishmentName}</p>
              <p className="text-sm font-medium mt-1">
                Total : {totals.totalPersons} couvert{totals.totalPersons > 1 ? "s" : ""} | {totals.totalReservations} réservation{totals.totalReservations > 1 ? "s" : ""}
              </p>
            </div>

            {/* No reservations */}
            {dayReservations.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                Aucune réservation pour cette date
              </p>
            )}

            {/* Reservations by slot */}
            {groupedBySlot.map(({ slot, reservations: slotReservations, totalPersons }) => (
              <div key={slot.start} className="mb-6">
                <div className="print-slot-header bg-muted/50 font-semibold py-2 px-3 rounded flex justify-between items-center">
                  <span>{getSlotLabel(slot)}</span>
                  <span className="text-sm">{totalPersons} couvert{totalPersons > 1 ? "s" : ""}</span>
                </div>
                
                <div className="mt-2 space-y-1">
                  {slotReservations.map((reservation) => (
                    <div 
                      key={reservation.id}
                      className="print-reservation flex items-start gap-2 py-1.5 px-2 border-b border-border/50 text-sm"
                    >
                      {/* Time */}
                      <span className="font-mono w-12 flex-shrink-0">{reservation.time}</span>
                      
                      {/* Table */}
                      {options.showTableNumbers && (
                        <span className="w-10 flex-shrink-0 font-medium">
                          {getTableName(reservation)}
                        </span>
                      )}
                      
                      {/* Persons */}
                      <span className="w-16 flex-shrink-0">
                        {reservation.persons} pers
                      </span>
                      
                      {/* Name */}
                      <span className="flex-1 min-w-0 truncate">
                        {getInitials(reservation.name)}
                      </span>
                      
                      {/* Notes */}
                      {options.showNotes && reservation.notes && (
                        <span className={`print-note flex items-center gap-1 flex-shrink-0 ${
                          hasAllergyNote(reservation.notes) 
                            ? "text-amber-600" 
                            : hasSpecialOccasion(reservation.notes)
                            ? "text-pink-600"
                            : "text-muted-foreground"
                        }`}>
                          {hasAllergyNote(reservation.notes) && <AlertTriangle className="w-3 h-3" />}
                          {hasSpecialOccasion(reservation.notes) && <Cake className="w-3 h-3" />}
                          <span className="text-xs max-w-32 truncate">{reservation.notes}</span>
                        </span>
                      )}

                      {/* Réponse à la question de l'établissement */}
                      {options.showNotes && reservation.customAnswers && reservation.customAnswers.answers.length > 0 && (
                        <span className="print-note flex-shrink-0 text-muted-foreground">
                          <span className="text-xs max-w-40 truncate">
                            {reservation.customAnswers.question} {reservation.customAnswers.answers.join(', ')}
                          </span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Summary */}
            {options.showSummary && dayReservations.length > 0 && (
              <div className="print-summary mt-6 pt-4 border-t-2 border-foreground">
                <h3 className="font-semibold mb-2">RÉCAPITULATIF</h3>
                <div className="space-y-1 text-sm">
                  <p>Total couverts : <strong>{totals.totalPersons}</strong></p>
                  {totals.withNotes > 0 && (
                    <p className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-600" />
                      Notes/allergies : {totals.withNotes} réservation{totals.withNotes > 1 ? "s" : ""} concernée{totals.withNotes > 1 ? "s" : ""}
                    </p>
                  )}
                  {totals.specialOccasions > 0 && (
                    <p className="flex items-center gap-1">
                      <Cake className="w-3 h-3 text-pink-600" />
                      Occasions spéciales : {totals.specialOccasions}
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Imprimé le {format(new Date(), "dd/MM/yyyy 'à' HH:mm", { locale: fr })}
                </p>
              </div>
            )}
          </div>
        </ResponsiveDialogBody>

        <ResponsiveDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Imprimer
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
